import React from 'react';
import { Button, DatePicker, Dropdown, MenuProps, Table } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hook';
import dayjs, { Dayjs } from 'dayjs';
import { exportHelper } from 'helpers/excelHelper';
import TableLoader from 'components/Sceletons/TableLoader';
import { IPaymentHistoryItem } from 'interfaces';
import { getPaymentHistoryColumns } from './paymentHistoryColumns';
import classes from './PaymentHistoryTable.module.scss';

const { RangePicker } = DatePicker;

interface PaymentHistoryTableProps {
  isLoading: boolean;
  data: IPaymentHistoryItem[];
  onOpenRefundModal?: (record: IPaymentHistoryItem) => void;
}

const PaymentHistoryTable: React.FC<PaymentHistoryTableProps> = ({
  isLoading,
  data,
  onOpenRefundModal,
}) => {
  const [dateRange, setDateRange] = React.useState<[Dayjs | null, Dayjs | null] | null>(null);

  const { userData } = useAppSelector((state) => state.userData);
  const { t } = useTranslation();

  const filteredData = React.useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return data;
    }
    const [startDate, endDate] = dateRange;
    return data.filter((item) => {
      if (!item.created_at) return false;
      const created = dayjs(item.created_at);
      return created.isAfter(startDate.startOf('day')) && created.isBefore(endDate.endOf('day'));
    });
  }, [data, dateRange]);

  const roomFilters = React.useMemo(() => {
    const uniqueRooms = Array.from(new Set(data.map((item) => item.room_name).filter(Boolean)));
    return uniqueRooms.map((room) => ({ text: room, value: room }));
  }, [data]);

  const cardFilters = React.useMemo(() => {
    const uniqueCards = Array.from(new Set(data.map((item) => item.payment_card).filter(Boolean)));
    return uniqueCards.map((card) => ({ text: card, value: card }));
  }, [data]);

  const columns = React.useMemo(() => {
    return getPaymentHistoryColumns({
      t,
      dateFormat: userData?.date_format || 'international',
      roomFilters,
      cardFilters,
      onOpenRefundModal: (record) => {
        if (onOpenRefundModal) {
          onOpenRefundModal(record);
        }
      },
    });
  }, [t, userData?.date_format, roomFilters, cardFilters, onOpenRefundModal]);

  const handleExportXLSX = () => {
    const exportColumns = columns
      .filter((col) => col.key !== 'actions')
      .map((col) => ({
        title: col.title as string,
        dataIndex: col.dataIndex,
        key: col.key,
      }));
    exportHelper(filteredData, exportColumns as any, 'PaymentHistory.xlsx');
  };

  const exportMenuItems: MenuProps['items'] = [
    {
      key: 'exportXLSX',
      label: t('Billing.export.xlsx'),
      onClick: handleExportXLSX,
    },
  ];

  if (isLoading) {
    return <TableLoader />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.topBar}>
        <RangePicker
          onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null])}
          format="DD.MM.YYYY"
        />
        <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      </div>
      <div className={classes.tableWrap}>
        <Table
          dataSource={filteredData}
          columns={columns}
          scroll={{
            y: 'calc(100vh - 219px)',
          }}
          pagination={false}
        />
      </div>
    </div>
  );
};

export default PaymentHistoryTable;

